import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { injectIntl, intlShape } from '@edx/frontend-platform/i18n';
import { Form, SearchField } from '@openedx/paragon';
import { debounce } from 'lodash';

import {
  getCourseFilterLanguages,
  getLibraryFilterCourses,
  getStudioHomeLibrariesParams,
} from '../../../data/selectors';
import { updateStudioHomeLibrariesCustomParams } from '../../../data/slice';
import {
  fetchCourseFilterLanguages,
  fetchLibraryData,
  fetchLibraryFilterCourses,
} from '../../../data/thunks';
import { LoadingSpinner } from '../../../../generic/Loading';
import CoursesLanguageFilterMenu from '../../courses-tab/courses-filters/courses-language-filter-menu';
import CoursesOrderFilterMenu from '../../courses-tab/courses-filters/courses-order-filter-menu';
import messages from './messages';
import '../../courses-tab/courses-filters/index.scss';

const regexOnlyWhiteSpaces = /^\s+$/;

const ORDER_BY_FILTER_TYPE = {
  azCourses: 'display_name',
  zaCourses: '-display_name',
  newestCourses: '-created',
  oldestCourses: 'created',
};

const LibrariesFilters = ({
  dispatch,
  isLoading,
  isLibraryFiltersEnabled,
  // injected
  intl,
}) => {
  const libraryFilterCourses = useSelector(getLibraryFilterCourses);
  const courseFilterLanguages = useSelector(getCourseFilterLanguages);
  const {
    course,
    search,
    order,
    language,
    cleanFilters,
  } = useSelector(getStudioHomeLibrariesParams);
  const [inputSearchValue, setInputSearchValue] = useState('');

  // Always-current filter snapshot so every action can copy params and change only its field.
  const filtersRef = useRef({
    search, order, language, course, cleanFilters,
  });
  filtersRef.current = {
    search, order, language, course, cleanFilters,
  };

  /**
   * Request-construction invariant: copy current request params, apply exactly one
   * (or intentionally related) modification, then dispatch.
   */
  const fetchLibraries = useCallback((filterOverrides) => {
    const current = filtersRef.current;
    const nextFilters = {
      search: current.search,
      order: current.order,
      language: current.language,
      course: current.course,
      ...filterOverrides,
    };

    filtersRef.current = {
      ...filtersRef.current,
      ...filterOverrides,
      cleanFilters: false,
    };

    dispatch(updateStudioHomeLibrariesCustomParams({
      isFiltered: true,
      cleanFilters: false,
      ...filterOverrides,
    }));
    dispatch(fetchLibraryData({
      search: nextFilters.search,
      order: nextFilters.order,
      language: nextFilters.language,
      course: nextFilters.course,
    }));
  }, [dispatch]);

  useEffect(() => {
    if (!isLibraryFiltersEnabled) {
      return;
    }
    dispatch(fetchLibraryFilterCourses(language));
  }, [dispatch, isLibraryFiltersEnabled, language]);

  useEffect(() => {
    if (isLibraryFiltersEnabled) {
      dispatch(fetchCourseFilterLanguages());
    }
  }, [dispatch, isLibraryFiltersEnabled]);

  const handleMenuFilterItemSelected = (filterType) => {
    const nextOrder = ORDER_BY_FILTER_TYPE[filterType];
    if (!nextOrder) {
      return;
    }
    fetchLibraries({ order: nextOrder });
  };

  const handleLanguageSelected = (languageValue) => {
    const languageParam = languageValue === 'allLanguages' ? undefined : languageValue;
    fetchLibraries({ language: languageParam });
  };

  const handleSearchLibrariesDebounced = useMemo(
    () => debounce((searchValueDebounced) => {
      const valueFormatted = searchValueDebounced.trim();
      const hasOnlySpaces = regexOnlyWhiteSpaces.test(searchValueDebounced);
      const current = filtersRef.current;

      if (valueFormatted !== current.search && !hasOnlySpaces && !current.cleanFilters) {
        fetchLibraries({
          search: valueFormatted.length > 0 ? valueFormatted : undefined,
        });
      }

      setInputSearchValue(searchValueDebounced);
    }, 400),
    [fetchLibraries],
  );

  useEffect(() => () => {
    handleSearchLibrariesDebounced.cancel();
  }, [handleSearchLibrariesDebounced]);

  const applyCourseFilter = (courseKey) => {
    fetchLibraries({ course: courseKey || undefined });
  };

  const handleCourseChange = (event) => {
    const courseKey = event.target.value;
    if (!courseKey) {
      if (course) {
        applyCourseFilter(undefined);
      }
      return;
    }
    if (courseKey !== course) {
      applyCourseFilter(courseKey);
    }
  };

  if (!isLibraryFiltersEnabled) {
    return null;
  }

  const allCoursesLabel = intl.formatMessage(messages.allCoursesLabel);

  return (
    <div className="d-flex mb-4">
      <div className="d-flex flex-row">
        <SearchField
          onChange={handleSearchLibrariesDebounced}
          value={cleanFilters ? '' : inputSearchValue}
          className="mr-4"
          data-testid="input-filter-libraries-search"
          placeholder="Search"
        />
        {isLoading && (
          <span className="search-field-loading" data-testid="loading-search-spinner">
            <LoadingSpinner size="sm" />
          </span>
        )}
      </div>

      <Form.Control
        as="select"
        value={course || ''}
        onChange={handleCourseChange}
        aria-label="library-course-filter"
        className="mr-4 dropdown-toggle-menu-items"
        data-testid="library-course-filter"
      >
        <option value="">{allCoursesLabel}</option>
        {libraryFilterCourses.map((c) => (
          <option key={c.courseKey} value={c.courseKey}>
            {c.displayName}
          </option>
        ))}
      </Form.Control>
      <CoursesLanguageFilterMenu
        key={`library-language-${String(cleanFilters)}`}
        languages={courseFilterLanguages}
        onItemMenuSelected={handleLanguageSelected}
      />
      <CoursesOrderFilterMenu
        key={`library-order-${String(cleanFilters)}`}
        onItemMenuSelected={handleMenuFilterItemSelected}
      />
    </div>
  );
};

LibrariesFilters.defaultProps = {
  isLoading: false,
  isLibraryFiltersEnabled: true,
};

LibrariesFilters.propTypes = {
  dispatch: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isLibraryFiltersEnabled: PropTypes.bool,
  intl: intlShape.isRequired,
};

export default injectIntl(LibrariesFilters);
